"use client";

import { useQuery } from "@tanstack/react-query";
import { UsersApi } from "@/openapi/generated";
import type { GetUserBaseResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createOpenApiConfiguration } from "@/lib/utils";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { isNotFoundError } from "@/lib/utils/media/is-not-found-error";
import { useDebounce } from "@/hooks/shared/useDebounce";
import {
  SIGNUP_EMAIL_STATUS,
  classifySignupEmailUser,
  type SignupEmailStatus,
} from "@/lib/utils/auth/signup-email-status";

const EMAIL_LOOKUP_DEBOUNCE_MS = 400;
const VALID_EMAIL = /\S+@\S+\.\S+/;

function isNotFoundLookupError(error: unknown): boolean {
  if (isNotFoundError(error)) return true;
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  return message.includes("not found") || message.includes("no user");
}

export function useSignupEmailLookup(email: string | undefined) {
  const normalized = (email ?? "").trim().toLowerCase();
  const isValid = VALID_EMAIL.test(normalized);
  const debouncedEmail = useDebounce(normalized, EMAIL_LOOKUP_DEBOUNCE_MS);
  const emailSettled = isValid && debouncedEmail === normalized;
  const tenantId = resolveEffectiveTenantNumber();

  const query = useQuery({
    queryKey: [QueryKeys.UserFindByEmail, tenantId, debouncedEmail],
    queryFn: async (): Promise<GetUserBaseResponse | null> => {
      const service = new UsersApi(createOpenApiConfiguration());
      try {
        return await service.apiUsersFindByEmailPost({
          getUserByEmailRequest: { email: debouncedEmail },
          xTenantID: tenantId,
        });
      } catch (error) {
        if (isNotFoundLookupError(error)) return null;
        throw error;
      }
    },
    enabled: emailSettled && tenantId > 0,
    retry: false,
    staleTime: 30_000,
  });

  const isChecking = isValid && (!emailSettled || query.isFetching);

  let status: SignupEmailStatus | "unknown" = SIGNUP_EMAIL_STATUS.new;
  if (!isValid) {
    status = SIGNUP_EMAIL_STATUS.new;
  } else if (!emailSettled || query.isFetching) {
    status = "unknown";
  } else if (query.isError) {
    status = SIGNUP_EMAIL_STATUS.new;
  } else {
    status = classifySignupEmailUser(query.data ?? null);
  }

  return {
    status,
    isChecking,
    user: status === "unknown" ? null : (query.data ?? null),
  };
}
